import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import WinnerRoll from './WinnerRoll';
import EditGiveaway from './EditGiveaway';
import VouchModal from './VouchModal';
import { AuthContext } from '../context/AuthContext';

const GiveawayDetails = ({ giveaway, hasEntered, isHost, onClose, onEntryComplete, onViewAnalytics }) => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, user } = useContext(AuthContext);

  const SUPPORTER_MULTIPLIERS = {
    bronze: { multiplier: 1.25, icon: '🥉', color: '#CD7F32' },
    silver: { multiplier: 1.5, icon: '🥈', color: '#C0C0C0' },
    gold: { multiplier: 1.75, icon: '🥇', color: '#FFD700' },
    platinum: { multiplier: 2, icon: '💎', color: '#E5E4E2' }
  };

  const getSupporterInfo = (tier) => {
    if (!tier) return { multiplier: 1, icon: '👤', color: '#666', tierName: 'None' };
    const lowerTier = tier.toLowerCase();
    return {
      ...SUPPORTER_MULTIPLIERS[lowerTier],
      tierName: tier.charAt(0).toUpperCase() + tier.slice(1)
    };
  };

  const supporterInfo = getSupporterInfo(user?.supporterTier);
  const [proofFiles, setProofFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showRollModal, setShowRollModal] = useState(false);
  const [rollType, setRollType] = useState('');
  const [showEndOptions, setShowEndOptions] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('fake_giveaway');
  const [reportDescription, setReportDescription] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [claimTracking, setClaimTracking] = useState(null);
  const [showClaimTracking, setShowClaimTracking] = useState(false);
  const [loadingClaimTracking, setLoadingClaimTracking] = useState(false);
  const [entryStatus, setEntryStatus] = useState(null);
  const [buyCount, setBuyCount] = useState(1);
  const [buyingEntries, setBuyingEntries] = useState(false);
  const [showVouchModal, setShowVouchModal] = useState(false);

  // Helper function to ensure URL has proper protocol
  const ensureProtocol = (url) => {
    if (!url) return '#';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  // Check if current user is a winner
  const isWinner = () => {
    if (!isAuthenticated || !user) return false;
    
    if (giveaway.winners && giveaway.winners.length > 0) {
      return giveaway.winners.some(w => String(w.userId) === String(user._id));
    }
    
    if (giveaway.currentWinner) {
      return String(giveaway.currentWinner.userId) === String(user._id);
    }
    
    return false;
  };

  // Fetch entry status with win chance
  const fetchEntryStatus = async () => {
    if (!isAuthenticated || !hasEntered) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/giveaways/${giveaway._id}/entry-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntryStatus(response.data);
    } catch (err) {
      console.error('Error fetching entry status:', err);
    }
  };

  // Buy additional entries
  const handleBuyEntries = async () => {
    setError('');
    setSuccess('');
    setBuyingEntries(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/giveaways/${giveaway._id}/buy-entries`,
        { count: buyCount },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(response.data.message);
      setBuyCount(1);
      fetchEntryStatus(); // Refresh entry status

      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Buy entries error:', err);
      setError(err.response?.data?.message || 'Failed to buy entries');
    } finally {
      setBuyingEntries(false);
    }
  };

  // Fetch entry status on mount and when hasEntered changes
  React.useEffect(() => {
    fetchEntryStatus();
  }, [hasEntered, isAuthenticated]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleProofChange = (index, file) => {
    if (file && file.size > 5 * 1024 * 1024) {
      setError('Each proof image must be less than 5MB');
      return;
    }
    setProofFiles({ ...proofFiles, [index]: file });
    setError('');
  };

  const handleSubmitEntry = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Check if user is authenticated
    if (!isAuthenticated) {
      setError('Please login to enter this giveaway');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please login to enter this giveaway');
      return;
    }

    setLoading(true);

    try {
      const requiredCount = giveaway.requirements.filter(r => r.required).length;
      const submittedCount = Object.keys(proofFiles).length;

      if (submittedCount < requiredCount) {
        setError(`Please provide proof for all ${requiredCount} required tasks`);
        setLoading(false);
        return;
      }

      const formData = new FormData();

      Object.keys(proofFiles).forEach((index) => {
        formData.append(`proof_${index}`, proofFiles[index]);
      });

      await axios.post(`/api/giveaways/${giveaway._id}/enter`, formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setSuccess('Successfully entered the giveaway! Good luck! 🎉');
      setTimeout(() => {
        onEntryComplete();
      }, 2000);
    } catch (err) {
      console.error('Entry error:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Please login to enter this giveaway');
      } else {
        setError(err.response?.data?.message || 'Failed to enter giveaway');
      }
    } finally {
      setLoading(false);
    }
  };



  const handleCloseGiveaway = async () => {
    if (!window.confirm('Are you sure you want to close this giveaway? No more entries will be accepted, but you can still roll a winner.')) {
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/giveaways/${giveaway._id}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Giveaway closed successfully! Redirecting...');
      setTimeout(() => {
        onEntryComplete(); // Refresh the giveaway list
        onClose(); // Close the modal
      }, 1500);
    } catch (err) {
      console.error('Close giveaway error:', err);
      setError(err.response?.data?.message || 'Failed to close giveaway');
    } finally {
      setLoading(false);
    }
  };

  const handleEndGiveaway = (type) => {
    setRollType(type);
    setShowRollModal(true);
    setShowEndOptions(false);
  };

  const handleRerollSpecificWinner = async (winnerPosition) => {
    if (!isHost) return;

    const confirmReroll = window.confirm(
      `Are you sure you want to reroll Winner #${winnerPosition}?\n\nThis will select a new winner for position ${winnerPosition}.`
    );

    if (!confirmReroll) return;

    const reason = window.prompt(
      `Optional: Enter a reason for rerolling Winner #${winnerPosition}:`,
      'Winner did not respond'
    );

    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `/api/giveaways/${giveaway._id}/reroll`,
        {
          rollType: 'normal',
          reason: reason || `Rerolled winner #${winnerPosition}`,
          winnerPosition: winnerPosition
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(`Winner #${winnerPosition} rerolled successfully! New winner: ${response.data.winner.username}`);

      // Refresh the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      console.error('Reroll error:', err);
      setError(err.response?.data?.message || 'Failed to reroll winner');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawEntry = async () => {
    if (!window.confirm('Are you sure you want to withdraw your entry? You will lose your chance to win.')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/giveaways/${giveaway._id}/leave`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Successfully withdrew entry.');
      setTimeout(() => {
        onEntryComplete();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Withdraw error:', err);
      setError(err.response?.data?.message || 'Failed to withdraw entry');
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async () => {
    if (!isHost) return;

    setLoadingParticipants(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/giveaways/${giveaway._id}/entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setParticipants(response.data);
      setShowParticipants(true);
    } catch (err) {
      console.error('Error fetching participants:', err);
      setError(err.response?.data?.message || 'Failed to load participants');
    } finally {
      setLoadingParticipants(false);
    }
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    setReportLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/giveaways/${giveaway._id}/report`, {
        reason: reportReason,
        description: reportDescription
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Report submitted successfully! Our team will review it shortly.');
      setShowReportModal(false);
      setReportReason('fake_giveaway');
      setReportDescription('');

      setTimeout(() => {
        setSuccess('');
      }, 5000);
    } catch (err) {
      console.error('Report error:', err);
      setError(err.response?.data?.message || 'Failed to submit report');
    } finally {
      setReportLoading(false);
    }
  };

  const fetchClaimTracking = async () => {
    if (!isHost && !isAdmin) return;

    setLoadingClaimTracking(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/giveaways/${giveaway._id}/claim-tracking`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaimTracking(response.data);
      setShowClaimTracking(true);
    } catch (err) {
      console.error('Error fetching claim tracking:', err);
      setError(err.response?.data?.message || 'Failed to load claim tracking');
    } finally {
      setLoadingClaimTracking(false);
    }
  };

  const formatTrackingDate = (dateString) => {
    if (!dateString) return 'Not yet';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOngoing = giveaway.status === 'ongoing' && new Date() < new Date(giveaway.endDate);
  const isClosed = giveaway.status === 'closed';
  const canRoll = isHost && (isClosed || giveaway.status === 'ended' || new Date() >= new Date(giveaway.endDate));
  const canClose = isHost && giveaway.status === 'ongoing';

  // Get CSS variable values for theme-aware colors
  const getCSSVar = (varName) => {
    return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: getCSSVar('--modal-overlay-bg'),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px',
        overflowY: 'auto'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: getCSSVar('--modal-bg'),
          borderRadius: '16px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '0'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Banner */}
        {giveaway.banner && (
          <div
            style={{
              width: '100%',
              height: '250px',
              backgroundColor: getCSSVar('--banner-placeholder-bg'),
              backgroundImage: `url(${giveaway.banner.url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '16px 16px 0 0',
              position: 'relative'
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                backgroundColor: getCSSVar('--modal-overlay-bg'),
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '0.9rem',
                fontFamily: 'monospace',
                fontWeight: 'bold'
              }}
            >
              {giveaway.giveawayId}
            </div>
          </div>
        )}

        <div style={{ padding: '30px' }}>
          {/* Title and Host */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
            <div style={{ flex: 1 }}>
              <h2 style={{ margin: '0 0 10px 0', fontSize: '2rem', color: getCSSVar('--text-dark') }}>{giveaway.title}</h2>
              <p style={{ margin: '0 0 10px 0', color: getCSSVar('--text-secondary'), fontSize: '1rem' }}>
                🎭 Hosted by <strong style={{ color: getCSSVar('--text-dark') }}>{giveaway.host.username}</strong>
              </p>
              
              {/* Trusted Giveaway Host Badge */}
              {giveaway.host.trustedGiveawayHost ? (
                <div className="trusted-giveaway-badge trusted" style={{ marginBottom: '20px' }}>
                  <span className="badge-icon">✓</span>
                  <span className="badge-text">Trusted Giveaway Host</span>
                </div>
              ) : (
                <div className="trusted-giveaway-badge unverified" style={{ marginBottom: '20px' }}>
                  <span className="badge-icon">⚠</span>
                  <span className="badge-text">This hoster has not been verified as a Trusted Giveaway Host yet</span>
                </div>
              )}
            </div>
            {/* Report Button (non-hosts only) */}
            {!isHost && isAuthenticated && (
              <button
                onClick={() => setShowReportModal(true)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: getCSSVar('--accent-red'),
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#E64A19'}
                onMouseLeave={(e) => e.target.style.backgroundColor = getCSSVar('--accent-red')}
              >
                🚨 Report
              </button>
            )}
          </div>

          {/* Status Badges */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {/* Debug badge for host */}
            {isHost && (
              <span
                style={{
                  backgroundColor: '#9C27B0',
                  color: 'white',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontFamily: 'monospace'
                }}
                title={`AutoPayout: ${giveaway.autoPayoutEnabled}, Status: ${giveaway.status}`}
              >
                🔍 AutoPay: {giveaway.autoPayoutEnabled ? 'ON' : 'OFF'} | Status: {giveaway.status}
              </span>
            )}
            <span
              style={{
                backgroundColor: giveaway.status === 'ongoing' ? getCSSVar('--accent-green') : getCSSVar('--accent-orange'),
                color: 'white',
                padding: '6px 14px',
                borderRadius: '20px',
                fontSize: '0.9rem',
                fontWeight: 'bold'
              }}
            >
              {giveaway.status === 'ongoing' ? '🔥 Live' : '⏱️ Ended'}
            </span>
            {hasEntered && (
              <span
                style={{
                  backgroundColor: getCSSVar('--accent-blue'),
                  color: 'white',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: 'bold'
                }}
              >
                ✓ You've Entered
              </span>
            )}
            {isHost && (
              <span
                style={{
                  backgroundColor: getCSSVar('--accent-purple'),
                  color: 'white',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: 'bold'
                }}
              >
                👑 You're the Host
              </span>
            )}
          </div>

          {/* Description */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem', color: getCSSVar('--text-dark') }}>📝 Description</h3>
            <p style={{ margin: 0, color: getCSSVar('--text-primary'), lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
              {giveaway.description}
            </p>
          </div>

          {/* Prize */}
          <div
            style={{
              backgroundColor: getCSSVar('--info-box-bg'),
              padding: '15px',
              borderRadius: '12px',
              marginBottom: '20px',
              border: `1px solid ${getCSSVar('--accent-orange')}`
            }}
          >
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem', color: getCSSVar('--accent-yellow') }}>
              🎁 Prize
            </h3>
            <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold', color: getCSSVar('--text-dark') }}>
              {giveaway.prize}
            </p>
          </div>

          {/* Special Thanks */}
          {giveaway.specialThanks && giveaway.specialThanks.name && (
            <div
              style={{
                backgroundColor: getCSSVar('--info-box-bg'),
                padding: '15px',
                borderRadius: '12px',
                marginBottom: '20px',
                border: `1px solid ${getCSSVar('--accent-purple')}`
              }}
            >
              <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem', color: getCSSVar('--accent-pink') }}>
                💝 Special Thanks
              </h3>
              <p style={{ margin: '0 0 5px 0', fontSize: '1.05rem', fontWeight: 'bold', color: getCSSVar('--text-dark') }}>
                {giveaway.specialThanks.name}
              </p>
              {giveaway.specialThanks.message && (
                <p style={{ margin: 0, color: getCSSVar('--text-secondary'), fontStyle: 'italic' }}>
                  {giveaway.specialThanks.message}
                </p>
              )}
            </div>
          )}

          {/* Stats */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '15px',
              marginBottom: '20px'
            }}
          >
            <div style={{ backgroundColor: getCSSVar('--info-box-bg'), padding: '15px', borderRadius: '12px', border: `1px solid ${getCSSVar('--modal-border')}` }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '0.85rem', color: getCSSVar('--text-gray') }}>Participants</p>
              <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: getCSSVar('--text-dark') }}>
                {giveaway.participantCount}
              </p>
            </div>
            <div style={{ backgroundColor: getCSSVar('--info-box-bg'), padding: '15px', borderRadius: '12px', border: `1px solid ${getCSSVar('--modal-border')}` }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '0.85rem', color: getCSSVar('--text-gray') }}>Ends On</p>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: 'bold', color: getCSSVar('--text-dark') }}>
                {formatDate(giveaway.endDate)}
              </p>
            </div>
            {isAuthenticated && (
              <div style={{ backgroundColor: getCSSVar('--info-box-bg'), padding: '15px', borderRadius: '12px', border: `2px solid ${supporterInfo.color}` }}>
                <p style={{ margin: '0 0 5px 0', fontSize: '0.85rem', color: getCSSVar('--text-gray') }}>
                  {supporterInfo.tierName !== 'None' ? `${supporterInfo.icon} Supporter Tier` : '👤 Current Status'}
                </p>
                <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold', color: supporterInfo.color }}>
                  {supporterInfo.tierName}
                </p>
                <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem', color: getCSSVar('--text-secondary') }}>
                  Entry Multiplier: <strong style={{ color: supporterInfo.color }}>{supporterInfo.multiplier}x</strong>
                </p>
              </div>
            )}
          </div>

          {/* Winner Display */}
          {(giveaway.winners?.length > 0 || giveaway.currentWinner) && (
            <>
              <div
                style={{
                  backgroundColor: getCSSVar('--success-bg'),
                  padding: '20px',
                  borderRadius: '12px',
                  marginBottom: '20px',
                  textAlign: 'center',
                  border: `2px solid ${getCSSVar('--accent-green')}`
                }}
              >
                <h3 style={{ margin: '0 0 10px 0', fontSize: '1.3rem', color: getCSSVar('--success-text') }}>
                  🏆 {giveaway.winners?.length > 1 ? `Winners Announced! (${giveaway.winners.length})` : 'Winner Announced!'}
                </h3>
                {giveaway.winners?.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                    {giveaway.winners.map((winner, index) => (
                      <p key={winner.userId || index} style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: getCSSVar('--success-text') }}>
                        {giveaway.winners.length > 1 && `#${winner.position}: `}{winner.username}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: getCSSVar('--success-text') }}>
                    {giveaway.currentWinner.username}
                  </p>
                )}
                {giveaway.rollLogs && giveaway.rollLogs.length > 0 && (
                  <p style={{ margin: '10px 0 0 0', fontSize: '0.9rem', color: getCSSVar('--text-gray') }}>
                    Rolled on {formatDate(giveaway.rollLogs[giveaway.rollLogs.length - 1].timestamp)}
                  </p>
                )}
                
                {/* Vouch Button for Winners */}
                {isWinner() && !isHost && (
                  <button
                    onClick={() => setShowVouchModal(true)}
                    style={{
                      marginTop: '15px',
                      padding: '12px 24px',
                      backgroundColor: '#FFC107',
                      color: '#000',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#FFD54F'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#FFC107'}
                  >
                    ⭐ Vouch for Host
                  </button>
                )}
              </div>

              {/* Proof Deletion Warning */}
              <div
                style={{
                  backgroundColor: getCSSVar('--error-bg'),
                  padding: '15px',
                  borderRadius: '12px',
                  marginBottom: '20px',
                  textAlign: 'center',
                  border: `2px solid ${getCSSVar('--accent-orange')}`
                }}
              >
                <h4 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: getCSSVar('--accent-orange'), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span>⚠️</span> Important Notice
                </h4>
                <p style={{ margin: 0, fontSize: '0.9rem', color: getCSSVar('--text-secondary'), lineHeight: '1.5' }}>
                  Entry proofs from non-winners will be automatically deleted 7 days after the winner is rolled to save storage space. Winner proofs are preserved.
                </p>
              </div>
            </>
          )}

          {/* Requirements */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '1.2rem', color: getCSSVar('--text-dark') }}>📋 Entry Requirements</h3>
            {giveaway.requirements.map((req, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: getCSSVar('--info-box-bg'),
                  padding: '12px 15px',
                  borderRadius: '8px',
                  marginBottom: '10px',
                  border: `1px solid ${getCSSVar('--modal-border')}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: getCSSVar('--text-dark') }}>
                      {index + 1}. {req.description}
                    </p>
                    {req.url && (
                      <a
                        href={ensureProtocol(req.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '0.85rem', color: getCSSVar('--accent-light-blue') }}
                      >
                        🔗 {req.url}
                      </a>
                    )}
                  </div>
                  {req.required && (
                    <span
                      style={{
                        backgroundColor: getCSSVar('--accent-red'),
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}
                    >
                      Required
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Withdraw Entry Button */}
          {hasEntered && isOngoing && !isHost && (
            <div style={{ textAlign: 'center', marginTop: '30px', marginBottom: '20px', borderTop: `1px solid ${getCSSVar('--modal-border')}`, paddingTop: '20px' }}>
              <p style={{ fontSize: '0.9rem', color: getCSSVar('--text-secondary'), marginBottom: '10px' }}>
                Changed your mind?
              </p>
              <button
                onClick={handleWithdrawEntry}
                disabled={loading}
                style={{
                  padding: '8px 20px',
                  backgroundColor: 'transparent',
                  color: getCSSVar('--accent-red'),
                  border: `1px solid ${getCSSVar('--accent-red')}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  transition: 'all 0.2s',
                  opacity: loading ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = getCSSVar('--accent-red');
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = getCSSVar('--accent-red');
                }}
              >
                {loading ? 'Withdrawing...' : '⚠️ Withdraw Entry'}
              </button>
            </div>
          )}

          {/* Entry Form or Login Prompt */}
          {isOngoing && !hasEntered && !isHost && (
            <>
              {!isAuthenticated ? (
                <div
                  style={{
                    backgroundColor: getCSSVar('--info-box-bg'),
                    padding: '30px',
                    borderRadius: '12px',
                    marginBottom: '20px',
                    border: `2px solid ${getCSSVar('--accent-orange')}`,
                    textAlign: 'center'
                  }}
                >
                  <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🔒</div>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '1.3rem', color: getCSSVar('--accent-yellow') }}>
                    Login Required
                  </h3>
                  <p style={{ margin: '0 0 20px 0', color: getCSSVar('--text-secondary'), fontSize: '1rem' }}>
                    Please login to enter this giveaway
                  </p>
                  <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <a
                      href={`/login?redirect=/giveaways/${giveaway._id}`}
                      style={{
                        display: 'inline-block',
                        padding: '12px 30px',
                        backgroundColor: getCSSVar('--accent-green'),
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = getCSSVar('--accent-green')}
                    >
                      Login to Enter
                    </a>
                    <a
                      href={`/api/social/twitter-login?giveawayId=${giveaway._id}`}
                      style={{
                        display: 'inline-block',
                        padding: '12px 30px',
                        backgroundColor: '#1DA1F2', // Twitter Blue
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#1a91da'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#1DA1F2'}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.84 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                      </svg>
                      Login with Twitter to Enter
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  <form onSubmit={handleSubmitEntry}>
                    <div
                      style={{
                        backgroundColor: getCSSVar('--info-box-bg'),
                        padding: '20px',
                        borderRadius: '12px',
                        marginBottom: '20px',
                        border: `1px solid ${getCSSVar('--accent-blue')}`
                      }}
                    >
                      <h3 style={{ margin: '0 0 15px 0', fontSize: '1.2rem', color: getCSSVar('--accent-light-blue') }}>
                        📸 Submit Proof Screenshots
                      </h3>
                      <p style={{ margin: '0 0 15px 0', color: getCSSVar('--text-secondary'), fontSize: '0.9rem' }}>
                        Upload screenshots showing you've completed each required task. The host will review
                        these before the winner is rolled.
                      </p>

                      {giveaway.requirements.map((req, index) => (
                        req.required && (
                          <div key={index} style={{ marginBottom: '15px' }}>
                            <label
                              style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontWeight: 'bold',
                                fontSize: '0.9rem',
                                color: '#ffffff'
                              }}
                            >
                              Proof for: {req.description}
                            </label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleProofChange(index, e.target.files[0])}
                              required
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '8px',
                                border: '1px solid #555',
                                backgroundColor: '#1a1a1a',
                                color: '#ffffff'
                              }}
                            />
                            {proofFiles[index] && (
                              <p style={{ margin: '5px 0 0 0', fontSize: '0.85rem', color: '#4CAF50' }}>
                                ✓ {proofFiles[index].name}
                              </p>
                            )}
                          </div>
                        )
                      ))}
                    </div>

                    {error && (
                      <div
                        style={{
                          backgroundColor: '#ffebee',
                          color: '#c62828',
                          padding: '12px',
                          borderRadius: '8px',
                          marginBottom: '15px'
                        }}
                      >
                        {error}
                      </div>
                    )}

                    {success && (
                      <div
                        style={{
                          backgroundColor: '#e8f5e9',
                          color: '#2e7d32',
                          padding: '12px',
                          borderRadius: '8px',
                          marginBottom: '15px'
                        }}
                      >
                        {success}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: '100%',
                        padding: '15px',
                        backgroundColor: loading ? '#ccc' : '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        marginBottom: '15px'
                      }}
                    >
                      {loading ? 'Submitting...' : '🎉 Enter Giveaway'}
                    </button>
                  </form>
                </>
              )}
            </>
          )}

          {/* Messages */}
          {hasEntered && (
            <>
              <div
                style={{
                  backgroundColor: '#e8f5e9',
                  padding: '15px',
                  borderRadius: '12px',
                  marginBottom: '15px',
                  textAlign: 'center'
                }}
              >
                <p style={{ margin: 0, color: '#2e7d32', fontWeight: 'bold' }}>
                  ✓ You've successfully entered this giveaway! Good luck! 🍀
                </p>
              </div>

              {/* Entry Stats and Buy More Entries */}
              {entryStatus && (
                <div
                  style={{
                    backgroundColor: getCSSVar('--card-bg'),
                    padding: '20px',
                    borderRadius: '12px',
                    marginBottom: '15px',
                    border: `2px solid ${getCSSVar('--btn-primary')}`
                  }}
                >
                  <h3 style={{
                    margin: '0 0 15px 0',
                    fontSize: '1.1rem',
                    color: getCSSVar('--text-primary'),
                    fontWeight: '600'
                  }}>
                    📊 Your Entry Stats
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                    <div style={{
                      textAlign: 'center',
                      padding: '15px',
                      backgroundColor: getCSSVar('--card-bg-secondary'),
                      borderRadius: '8px',
                      border: `1px solid ${getCSSVar('--border')}`
                    }}>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold', color: getCSSVar('--btn-primary') }}>
                        {entryStatus.entry?.entryCount || 1}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: getCSSVar('--text-secondary'), marginTop: '5px' }}>
                        Your Entries
                      </div>
                    </div>
                    <div style={{
                      textAlign: 'center',
                      padding: '15px',
                      backgroundColor: getCSSVar('--card-bg-secondary'),
                      borderRadius: '8px',
                      border: `1px solid ${getCSSVar('--border')}`
                    }}>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold', color: getCSSVar('--accent-green') }}>
                        {entryStatus.winChance}%
                      </div>
                      <div style={{ fontSize: '0.9rem', color: getCSSVar('--text-secondary'), marginTop: '5px' }}>
                        Win Chance
                      </div>
                    </div>
                  </div>

                  <div style={{
                    padding: '12px',
                    backgroundColor: getCSSVar('--info-box-bg') || getCSSVar('--card-bg-secondary'),
                    borderRadius: '8px',
                    marginBottom: '15px',
                    textAlign: 'center',
                    fontSize: '0.85rem',
                    color: getCSSVar('--text-primary'),
                    border: `1px solid ${getCSSVar('--border')}`
                  }}>
                    Total entries in giveaway: <strong style={{ color: getCSSVar('--btn-primary') }}>{entryStatus.totalEntries}</strong>
                  </div>

                  {/* Buy More Entries */}
                  {isOngoing && entryStatus.entry?.entryCount < 10 && (
                    <div style={{
                      borderTop: `1px solid ${getCSSVar('--border')}`,
                      paddingTop: '15px',
                      marginTop: '10px'
                    }}>
                      <h4 style={{
                        margin: '0 0 10px 0',
                        fontSize: '1rem',
                        color: getCSSVar('--text-primary'),
                        fontWeight: '600'
                      }}>
                        🎯 Buy More Entries
                      </h4>
                      <p style={{
                        margin: '0 0 15px 0',
                        fontSize: '0.85rem',
                        color: getCSSVar('--text-secondary'),
                        lineHeight: '1.4'
                      }}>
                        Increase your chances! Each additional entry costs 100 points. Maximum 10 entries total.
                      </p>

                      <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
                        <input
                          type="number"
                          min="1"
                          max={10 - (entryStatus.entry?.entryCount || 1)}
                          value={buyCount}
                          onChange={(e) => setBuyCount(Math.min(10 - (entryStatus.entry?.entryCount || 1), Math.max(1, parseInt(e.target.value) || 1)))}
                          style={{
                            padding: '12px',
                            borderRadius: '8px',
                            border: `2px solid ${getCSSVar('--border')}`,
                            backgroundColor: getCSSVar('--input-bg'),
                            color: getCSSVar('--input-text') || getCSSVar('--text-primary'),
                            fontSize: '1rem',
                            width: '80px',
                            textAlign: 'center',
                            fontWeight: '600',
                            outline: 'none',
                            transition: 'border-color 0.2s'
                          }}
                          onFocus={(e) => e.target.style.borderColor = getCSSVar('--btn-primary')}
                          onBlur={(e) => e.target.style.borderColor = getCSSVar('--border')}
                        />
                        <button
                          onClick={handleBuyEntries}
                          disabled={buyingEntries}
                          style={{
                            flex: 1,
                            padding: '12px 20px',
                            backgroundColor: buyingEntries ? getCSSVar('--btn-secondary') : getCSSVar('--btn-primary'),
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: buyingEntries ? 'not-allowed' : 'pointer',
                            fontSize: '0.95rem',
                            fontWeight: 'bold',
                            transition: 'all 0.2s',
                            opacity: buyingEntries ? 0.6 : 1,
                            boxShadow: buyingEntries ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                          }}
                          onMouseEnter={(e) => {
                            if (!buyingEntries) {
                              e.target.style.transform = 'translateY(-1px)';
                              e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = buyingEntries ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)';
                          }}
                        >
                          {buyingEntries ? 'Buying...' : `Buy ${buyCount} ${buyCount === 1 ? 'Entry' : 'Entries'} (${buyCount * 100} points)`}
                        </button>
                      </div>
                    </div>
                  )}

                  {entryStatus.entry?.entryCount >= 10 && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: getCSSVar('--warning-bg') || 'rgba(255, 193, 7, 0.1)',
                      borderRadius: '8px',
                      textAlign: 'center',
                      fontSize: '0.85rem',
                      color: getCSSVar('--warning-text') || getCSSVar('--text-primary'),
                      border: `1px solid ${getCSSVar('--warning-border') || getCSSVar('--accent-yellow')}`,
                      fontWeight: '500'
                    }}>
                      🏆 Maximum entries reached! You have the best chance possible!
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {!isOngoing && !hasEntered && !isHost && (
            <div
              style={{
                backgroundColor: '#fff3e0',
                padding: '15px',
                borderRadius: '12px',
                marginBottom: '15px',
                textAlign: 'center'
              }}
            >
              <p style={{ margin: 0, color: '#ef6c00', fontWeight: 'bold' }}>
                This giveaway has ended. No more entries are being accepted.
              </p>
            </div>
          )}

          {/* Host Controls */}
          {isHost && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '1.2rem', color: '#ffffff' }}>
                👑 Host Controls
              </h3>

              {/* Analytics Button - Always visible to host */}
              {onViewAnalytics && (
                <button
                  onClick={() => {
                    onViewAnalytics(giveaway._id);
                  }}
                  style={{
                    width: '100%',
                    padding: '15px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  📊 View Analytics
                </button>
              )}

              {/* Edit Giveaway Button - Show while giveaway is ongoing and hasn't ended */}
              {isOngoing && (
                <button
                  onClick={() => setShowEditModal(true)}
                  style={{
                    width: '100%',
                    padding: '15px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    marginBottom: '10px'
                  }}
                >
                  ✏️ Edit Giveaway
                </button>
              )}

              {/* Close Giveaway Button */}
              {canClose && (
                <button
                  onClick={handleCloseGiveaway}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '15px',
                    backgroundColor: loading ? '#ccc' : '#FF9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    marginBottom: '10px'
                  }}
                >
                  🔒 Close Giveaway (Stop Entries)
                </button>
              )}

              {/* End Giveaway Button */}
              {canRoll && !showEndOptions && (
                <button
                  onClick={() => setShowEndOptions(true)}
                  style={{
                    width: '100%',
                    padding: '15px',
                    backgroundColor: '#9C27B0',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    marginBottom: '10px'
                  }}
                >
                  🎲 End Giveaway & Roll Winner
                </button>
              )}

              {/* End Options */}
              {showEndOptions && canRoll && (
                <div
                  style={{
                    backgroundColor: '#2d2d2d',
                    padding: '20px',
                    borderRadius: '12px',
                    marginBottom: '15px',
                    border: '1px solid #9C27B0'
                  }}
                >
                  <h4 style={{ margin: '0 0 15px 0', fontSize: '1.1rem', color: '#BA68C8' }}>
                    Choose Roll Method:
                  </h4>

                  <button
                    onClick={() => handleEndGiveaway('normal')}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: 'bold',
                      marginBottom: '10px'
                    }}
                  >
                    ⚡ Direct Roll (Instant Winner)
                  </button>

                  <button
                    onClick={() => handleEndGiveaway('spin_wheel')}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: 'bold',
                      marginBottom: '10px'
                    }}
                  >
                    🎡 Spin the Wheel
                  </button>

                  <button
                    onClick={() => setShowEndOptions(false)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#666',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 'bold'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}

              {isClosed && (
                <div
                  style={{
                    backgroundColor: '#2d2d2d',
                    padding: '15px',
                    borderRadius: '12px',
                    marginBottom: '15px',
                    textAlign: 'center',
                    border: '1px solid #FF9800'
                  }}
                >
                  <p style={{ margin: 0, color: '#FFA726', fontWeight: 'bold' }}>
                    🔒 This giveaway is closed. No more entries are being accepted.
                  </p>
                </div>
              )}

              {/* View Participants Button */}
              <button
                onClick={fetchParticipants}
                disabled={loadingParticipants}
                style={{
                  width: '100%',
                  padding: '15px',
                  backgroundColor: loadingParticipants ? '#ccc' : '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: loadingParticipants ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  marginBottom: '10px'
                }}
              >
                {loadingParticipants ? 'Loading...' : `👥 View All Participants (${giveaway.participantCount})`}
              </button>

              {/* View Claim Tracking Button (for host and admins, if auto-payout enabled and giveaway is rolled) */}
              {giveaway.autoPayoutEnabled && giveaway.status === 'rolled' && (isHost || isAdmin) && (
                <button
                  onClick={() => {
                    console.log('Giveaway data:', {
                      autoPayoutEnabled: giveaway.autoPayoutEnabled,
                      status: giveaway.status,
                      hasWinners: !!giveaway.winners,
                      winnersCount: giveaway.winners?.length
                    });
                    fetchClaimTracking();
                  }}
                  disabled={loadingClaimTracking}
                  style={{
                    width: '100%',
                    padding: '15px',
                    backgroundColor: loadingClaimTracking ? '#ccc' : '#00BCD4',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: loadingClaimTracking ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    marginBottom: '10px'
                  }}
                >
                  {loadingClaimTracking ? '⏳ Loading...' : '📊 View Claim Tracking'}
                </button>
              )}
            </div>
          )}

          {/* Participants List (Host Only) */}
          {isHost && showParticipants && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#ffffff' }}>
                  👥 All Participants ({participants.length})
                </h3>
                <button
                  onClick={() => setShowParticipants(false)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#666',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  Hide
                </button>
              </div>

              {participants.length === 0 ? (
                <div
                  style={{
                    backgroundColor: '#2d2d2d',
                    padding: '30px',
                    borderRadius: '12px',
                    textAlign: 'center',
                    border: '1px solid #3d3d3d'
                  }}
                >
                  <p style={{ margin: 0, color: '#b0b0b0' }}>
                    No participants yet. Share this giveaway to get more entries!
                  </p>
                </div>
              ) : (
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {participants.map((entry, index) => (
                    <div
                      key={entry._id}
                      style={{
                        backgroundColor: '#2d2d2d',
                        padding: '15px',
                        borderRadius: '12px',
                        marginBottom: '10px',
                        border: '1px solid #3d3d3d',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => setSelectedParticipant(selectedParticipant === entry._id ? null : entry._id)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#353535'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2d2d2d'}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                            <p style={{ margin: 0, fontSize: '1rem', fontWeight: 'bold', color: '#ffffff' }}>
                              {index + 1}. {entry.username || entry.user?.username || 'Anonymous'}
                            </p>
                            {(entry.username || entry.user?.username) && (
                              <a
                                href={`/profile/${entry.username || entry.user?.username}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/profile/${entry.username || entry.user?.username}`);
                                  onClose();
                                }}
                                style={{
                                  color: '#64B5F6',
                                  textDecoration: 'none',
                                  fontSize: '0.85rem',
                                  fontWeight: '500',
                                  padding: '2px 8px',
                                  backgroundColor: 'rgba(100, 181, 246, 0.1)',
                                  borderRadius: '4px',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'rgba(100, 181, 246, 0.2)';
                                  e.target.style.color = '#90CAF9';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'rgba(100, 181, 246, 0.1)';
                                  e.target.style.color = '#64B5F6';
                                }}
                              >
                                Profile
                              </a>
                            )}
                          </div>
                          <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>
                            Entered: {new Date(entry.entryDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span
                            style={{
                              backgroundColor: getCSSVar('--primary'),
                              color: 'white',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {entry.entryCount || 1} {entry.entryCount === 1 ? 'Entry' : 'Entries'}
                          </span>
                          <span
                            style={{
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {entry.proofs?.length || 0} Proofs
                          </span>
                          <span style={{ color: '#64B5F6', fontSize: '0.9rem' }}>
                            {selectedParticipant === entry._id ? '▼' : '▶'}
                          </span>
                        </div>
                      </div>

                      {/* Proof Screenshots (Expandable) */}
                      {selectedParticipant === entry._id && entry.proofs && entry.proofs.length > 0 && (
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #444' }}>
                          <h4 style={{ margin: '0 0 10px 0', fontSize: '0.95rem', color: '#64B5F6' }}>
                            📸 Proof Screenshots:
                          </h4>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                              gap: '10px'
                            }}
                          >
                            {entry.proofs.map((proof, proofIndex) => (
                              <div key={proofIndex} style={{ position: 'relative' }}>
                                <a
                                  href={proof.screenshot?.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    display: 'block',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    border: '2px solid #444',
                                    transition: 'transform 0.2s',
                                    cursor: 'pointer'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                >
                                  <img
                                    src={proof.screenshot?.url}
                                    alt={`Proof ${proofIndex + 1}`}
                                    style={{
                                      width: '100%',
                                      height: '150px',
                                      objectFit: 'cover',
                                      display: 'block'
                                    }}
                                  />
                                </a>
                                <div
                                  style={{
                                    position: 'absolute',
                                    bottom: '5px',
                                    left: '5px',
                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    padding: '3px 8px',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  Task #{proof.requirementIndex + 1}
                                </div>
                              </div>
                            ))}
                          </div>
                          <p style={{ fontSize: '0.75rem', color: '#888', marginTop: '10px', textAlign: 'center' }}>
                            Click images to view full size
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Winner Display (After Roll) */}
          {(giveaway.status === 'rolled' || giveaway.status === 'ended') && (giveaway.winners?.length > 0 || giveaway.currentWinner) && (
            <div
              style={{
                backgroundColor: '#2d2d2d',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '20px',
                border: '2px solid #9C27B0'
              }}
            >
              <h3 style={{ margin: '0 0 15px 0', fontSize: '1.3rem', color: '#BA68C8', textAlign: 'center' }}>
                🏆 {giveaway.winners?.length > 1 ? `Winners Announcement (${giveaway.winners.length})` : 'Winner Announcement'}
              </h3>

              {/* Display multiple winners or single winner */}
              {giveaway.winners?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {giveaway.winners.map((winner, index) => (
                    <div key={winner.userId || index} style={{
                      backgroundColor: '#1a1a1a',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #444'
                    }}>
                      {giveaway.winners.length > 1 && (
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', color: '#64B5F6', textAlign: 'center' }}>
                          Winner #{winner.position}
                        </h4>
                      )}
                      <div style={{ textAlign: 'center', marginBottom: winner.winnerProofs?.length > 0 ? '15px' : '0' }}>
                        <p
                          onClick={() => {
                            navigate(`/profile/${winner.username}`);
                            onClose();
                          }}
                          style={{
                            fontSize: '1.2rem',
                            fontWeight: 'bold',
                            color: '#BA68C8',
                            margin: '5px 0',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'inline-block'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#CE93D8';
                            e.currentTarget.style.textDecoration = 'underline';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#BA68C8';
                            e.currentTarget.style.textDecoration = 'none';
                          }}
                        >
                          @{winner.username}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#999', margin: '5px 0', fontStyle: 'italic' }}>
                          Click to view profile
                        </p>

                        {/* Reroll Individual Winner Button (Host Only) */}
                        {isHost && giveaway.winners.length > 1 && (
                          <button
                            onClick={() => handleRerollSpecificWinner(winner.position)}
                            style={{
                              marginTop: '10px',
                              padding: '8px 16px',
                              backgroundColor: '#FF9800',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: 'bold',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#F57C00'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#FF9800'}
                          >
                            🔄 Reroll This Winner
                          </button>
                        )}
                      </div>

                      {/* Winner's Proof Screenshots */}
                      {winner.winnerProofs && winner.winnerProofs.length > 0 && (
                        <div>
                          <h4 style={{ color: '#ffffff', fontSize: '0.9rem', marginBottom: '10px', textAlign: 'center' }}>
                            📸 Entry Proofs:
                          </h4>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                              gap: '10px'
                            }}
                          >
                            {winner.winnerProofs.map((proofUrl, pIndex) => (
                              <a
                                key={pIndex}
                                href={proofUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'block',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  border: '2px solid #444',
                                  transition: 'transform 0.2s',
                                  cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                              >
                                <img
                                  src={proofUrl}
                                  alt={`Proof ${pIndex + 1}`}
                                  style={{
                                    width: '100%',
                                    height: '120px',
                                    objectFit: 'cover',
                                    display: 'block'
                                  }}
                                />
                              </a>
                            ))}
                          </div>
                          <p style={{ fontSize: '0.75rem', color: '#888', marginTop: '10px', textAlign: 'center' }}>
                            Click images to view full size
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                // Fallback to currentWinner for backward compatibility
                <>
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <p
                      onClick={() => {
                        navigate(`/profile/${giveaway.currentWinner.username}`);
                        onClose();
                      }}
                      style={{
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        color: '#BA68C8',
                        margin: '5px 0',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'inline-block'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#CE93D8';
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#BA68C8';
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                    >
                      @{giveaway.currentWinner.username}
                    </p>
                    <p style={{ fontSize: '0.75rem', color: '#999', margin: '5px 0', fontStyle: 'italic' }}>
                      Click to view profile
                    </p>
                  </div>

                  {/* Winner's Proof Screenshots */}
                  {giveaway.winnerProofs && giveaway.winnerProofs.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ color: '#ffffff', fontSize: '1rem', marginBottom: '10px' }}>
                        📸 Winner's Entry Proofs:
                      </h4>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                          gap: '10px'
                        }}
                      >
                        {giveaway.winnerProofs.map((proofUrl, index) => (
                          <a
                            key={index}
                            href={proofUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'block',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '2px solid #444',
                              transition: 'transform 0.2s',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          >
                            <img
                              src={proofUrl}
                              alt={`Proof ${index + 1}`}
                              style={{
                                width: '100%',
                                height: '120px',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                            />
                          </a>
                        ))}
                      </div>
                      <p style={{ fontSize: '0.75rem', color: '#888', marginTop: '10px', textAlign: 'center' }}>
                        Click images to view full size
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Host and Admin Actions */}
              {(isHost || isAdmin) && giveaway.autoPayoutEnabled && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '10px' }}>
                  {/* Claim Tracking Button (for host and admins, only if auto-payout enabled) */}
                  <button
                    onClick={fetchClaimTracking}
                    disabled={loadingClaimTracking}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: loadingClaimTracking ? '#555' : '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: loadingClaimTracking ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {loadingClaimTracking ? '⏳ Loading...' : '📊 View Claim Tracking'}
                  </button>
                </div>
              )}

              {/* Host Actions */}
              {isHost && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

                  {/* Reroll Button */}
                  <button
                    onClick={() => {
                      setShowRollModal(true);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#FF5722',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: 'bold'
                    }}
                  >
                    🔄 Reroll Winner
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Claim Tracking Display (Host and Admins) */}
          {showClaimTracking && claimTracking && (isHost || isAdmin) && (
            <div
              style={{
                backgroundColor: '#2d2d2d',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '20px',
                border: '2px solid #2196F3'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, fontSize: '1.3rem', color: '#64B5F6' }}>
                  📊 Claim Tracking
                </h3>
                <button
                  onClick={() => setShowClaimTracking(false)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#999',
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    padding: '0',
                    width: '30px',
                    height: '30px'
                  }}
                >
                  ✕
                </button>
              </div>

              {claimTracking.tracking && claimTracking.tracking.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {claimTracking.tracking.map((winner) => (
                    <div
                      key={winner.position}
                      style={{
                        backgroundColor: '#1a1a1a',
                        padding: '15px',
                        borderRadius: '8px',
                        border: '1px solid #444'
                      }}
                    >
                      <div style={{ marginBottom: '12px', borderBottom: '1px solid #444', paddingBottom: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h4 style={{ margin: 0, color: '#BA68C8', fontSize: '1.1rem' }}>
                            {giveaway.numberOfWinners > 1 ? `Winner #${winner.position}` : 'Winner'}: @{winner.username}
                          </h4>
                          <span
                            style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              backgroundColor: winner.keySent ? '#4CAF50' : winner.keyClaimStatus === 'expired' ? '#F44336' : '#FF9800',
                              color: 'white'
                            }}
                          >
                            {winner.keySent ? '✓ Claimed' : winner.keyClaimStatus === 'expired' ? 'Expired' : 'Pending'}
                          </span>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.9rem' }}>
                        <div>
                          <p style={{ margin: '5px 0', color: '#888' }}>
                            <strong style={{ color: '#64B5F6' }}>🎲 Rolled:</strong>
                          </p>
                          <p style={{ margin: '5px 0 5px 20px', color: '#fff' }}>
                            {formatTrackingDate(winner.rolledAt)}
                          </p>
                        </div>

                        <div>
                          <p style={{ margin: '5px 0', color: '#888' }}>
                            <strong style={{ color: '#64B5F6' }}>📨 Claim DM Sent:</strong>
                          </p>
                          <p style={{ margin: '5px 0 5px 20px', color: winner.claimMessageSentAt ? '#fff' : '#666' }}>
                            {formatTrackingDate(winner.claimMessageSentAt)}
                          </p>
                        </div>

                        <div>
                          <p style={{ margin: '5px 0', color: '#888' }}>
                            <strong style={{ color: '#64B5F6' }}>✅ Winner Claimed:</strong>
                          </p>
                          <p style={{ margin: '5px 0 5px 20px', color: winner.claimResponseAt ? '#4CAF50' : '#666', fontWeight: winner.claimResponseAt ? 'bold' : 'normal' }}>
                            {formatTrackingDate(winner.claimResponseAt)}
                          </p>
                        </div>

                        <div>
                          <p style={{ margin: '5px 0', color: '#888' }}>
                            <strong style={{ color: '#64B5F6' }}>⏰ Claim Deadline:</strong>
                          </p>
                          <p style={{ margin: '5px 0 5px 20px', color: winner.claimDeadline ? '#fff' : '#666' }}>
                            {formatTrackingDate(winner.claimDeadline)}
                          </p>
                        </div>
                      </div>

                      {winner.responseTimeMinutes !== null && (
                        <div
                          style={{
                            marginTop: '12px',
                            padding: '10px',
                            backgroundColor: '#0d47a1',
                            borderRadius: '6px',
                            textAlign: 'center'
                          }}
                        >
                          <p style={{ margin: 0, color: '#fff', fontSize: '0.9rem' }}>
                            ⚡ <strong>Response Time:</strong> {winner.responseTimeMinutes} minute{winner.responseTimeMinutes !== 1 ? 's' : ''}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#888', textAlign: 'center', margin: '20px 0' }}>
                  No claim tracking data available. This giveaway may not have auto-payout enabled.
                </p>
              )}
            </div>
          )}

          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#9e9e9e',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Winner Roll Modal */}
      {showRollModal && (
        <WinnerRoll
          giveaway={giveaway}
          initialRollType={rollType}
          onClose={() => {
            setShowRollModal(false);
            setRollType('');
          }}
          onRollComplete={() => {
            setShowRollModal(false);
            setRollType('');
            onEntryComplete();
          }}
        />
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
          onClick={() => setShowReportModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1a1a1a',
              borderRadius: '16px',
              maxWidth: '500px',
              width: '100%',
              padding: '30px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 20px 0', fontSize: '1.5rem', color: '#ffffff' }}>
              🚨 Report Giveaway
            </h2>

            <form onSubmit={handleReportSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#ffffff', fontWeight: 'bold' }}>
                  Reason *
                </label>
                <select
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #555',
                    backgroundColor: '#2d2d2d',
                    color: '#ffffff',
                    fontSize: '1rem'
                  }}
                >
                  <option value="fake_giveaway">Fake Giveaway</option>
                  <option value="scam">Scam / Fraud</option>
                  <option value="inappropriate_content">Inappropriate Content</option>
                  <option value="misleading">Misleading Information</option>
                  <option value="spam">Spam</option>
                  <option value="offensive">Offensive / Hate Speech</option>
                  <option value="duplicate">Duplicate Giveaway</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#ffffff', fontWeight: 'bold' }}>
                  Description * (Please provide details)
                </label>
                <textarea
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="Please explain why you're reporting this giveaway..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #555',
                    backgroundColor: '#2d2d2d',
                    color: '#ffffff',
                    fontSize: '1rem',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
                <p style={{ margin: '5px 0 0 0', fontSize: '0.85rem', color: '#888' }}>
                  {reportDescription.length}/500 characters
                </p>
              </div>

              {error && (
                <div
                  style={{
                    backgroundColor: '#ffebee',
                    color: '#c62828',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '15px'
                  }}
                >
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowReportModal(false);
                    setReportReason('fake_giveaway');
                    setReportDescription('');
                    setError('');
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#666',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={reportLoading}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: reportLoading ? '#ccc' : '#FF5722',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: reportLoading ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  {reportLoading ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Giveaway Modal */}
      {showEditModal && (
        <EditGiveaway
          giveaway={giveaway}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            // Refresh the giveaway data
            if (onEntryComplete) {
              onEntryComplete();
            }
          }}
        />
      )}

      {/* Vouch Modal */}
      {showVouchModal && (
        <VouchModal
          toUserId={giveaway.host?.userId || giveaway.host?._id || giveaway.host}
          toUsername={giveaway.host?.username || 'Host'}
          giveawayId={giveaway._id || giveaway.id}
          gameName={giveaway.prize}
          onClose={() => setShowVouchModal(false)}
          onSuccess={() => {
            setShowVouchModal(false);
            setSuccess('Thank you for vouching! Your feedback helps build trust in the community.');
            setTimeout(() => setSuccess(''), 5000);
          }}
        />
      )}
    </div>
  );
};

export default GiveawayDetails;
